import type { FilterState } from '../filterTypes';

type BedroomState = FilterState['bedrooms'];

/**
 * Expands a bedroom range to the comma-delimited API format.
 * { min: 2, max: null } → "2,3,4,5,6,7,8plus"
 * { min: 2, max: 4 }   → "2,3,4" (bounded range)
 * { min: 0, max: 0 }   → "0" (Studio)
 * { min: 3, max: 3 }   → "3"
 * { min: null, ... }   → undefined (any beds, omit param)
 */
export function expandBedroomRange(bedrooms: BedroomState): string | undefined {
  if (bedrooms.min === null) return undefined;

  if (bedrooms.min === bedrooms.max) {
    return String(bedrooms.min);
  }

  const cap = bedrooms.max ?? 7;
  const values: Array<string> = [];
  for (let i = bedrooms.min; i <= cap; i++) {
    values.push(String(i));
  }
  if (bedrooms.max === null) values.push('8plus');
  return values.join(',');
}

/**
 * Parses beds from URL param.
 * "2-8plus" → { min: 2, max: null }
 * "2-4"     → { min: 2, max: 4 } (bounded range)
 * "0"       → { min: 0, max: 0 } (Studio)
 * "3"       → { min: 3, max: 3 }
 */
export function parseBedsFromUrl(value: string): BedroomState {
  if (value.includes('-')) {
    const [minStr, maxStr] = value.split('-');
    const min = parseInt(minStr, 10);
    if (isNaN(min) || min < 0) return { min: null, max: null };
    if (maxStr === '8plus') return { min, max: null };
    const max = parseInt(maxStr, 10);
    if (isNaN(max) || max < min) return { min, max: null };
    return { min, max };
  }

  const exact = parseInt(value, 10);
  if (isNaN(exact) || exact < 0) return { min: null, max: null };
  return { min: exact, max: exact };
}

/**
 * Serializes beds to URL param.
 * { min: 2, max: null } → "2-8plus"
 * { min: 2, max: 4 }   → "2-4" (bounded range)
 * { min: 0, max: 0 }   → "0" (Studio)
 * { min: 3, max: 3 }   → "3"
 * { min: null, ... }   → undefined (omit from URL)
 */
export function serializeBedsToUrl(bedrooms: BedroomState): string | undefined {
  if (bedrooms.min === null) return undefined;

  if (bedrooms.min === bedrooms.max) {
    return String(bedrooms.min);
  }

  if (bedrooms.max !== null) return `${bedrooms.min}-${bedrooms.max}`;
  return `${bedrooms.min}-8plus`;
}
